import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/endpoints';
import {
  CompleteRegistrationRequest,
  LoginCredentials,
  MessageResponse,
  RegistrationUserInfoResponse,
  SendOtpRequest,
  SignupRequest,
  UserResponse,
  VerifyOtpRequest,
} from '../types/auth.types';

export const authService = {
  /**
   * Login user - returns UserResponse, sets session cookie
   */
  login: async (credentials: LoginCredentials): Promise<UserResponse> => {
    const response = await apiClient.post<UserResponse>(
      API_ENDPOINTS.LOGIN,
      credentials
    );
    return response.data;
  },

  /**
   * Send login OTP to email
   */
  sendOtp: async (data: SendOtpRequest): Promise<MessageResponse> => {
    const response = await apiClient.post<MessageResponse>(
      API_ENDPOINTS.SEND_OTP,
      data
    );
    return response.data;
  },

  /**
   * Resend login OTP (rate limited to once per minute)
   */
  resendOtp: async (data: SendOtpRequest): Promise<MessageResponse> => {
    const response = await apiClient.post<MessageResponse>(
      API_ENDPOINTS.RESEND_OTP,
      data
    );
    return response.data;
  },

  /**
   * Verify login OTP - returns UserResponse, sets session cookie
   */
  verifyOtp: async (data: VerifyOtpRequest): Promise<UserResponse> => {
    const response = await apiClient.post<UserResponse>(
      API_ENDPOINTS.VERIFY_OTP,
      data
    );
    return response.data;
  },

  /**
   * Sign up new user - returns SignupResponse
   */
  signup: async (data: SignupRequest): Promise<{ message: string; userId: string }> => {
    const response = await apiClient.post<{ message: string; userId: string }>(
      API_ENDPOINTS.SIGNUP,
      data
    );
    return response.data;
  },

  /**
   * Legacy register method for backward compatibility
   */
  register: async (credentials: SignupRequest): Promise<{ message: string; userId: string }> => {
    return authService.signup(credentials);
  },

  /**
   * Logout user - clears session cookie
   */
  logout: async (): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(API_ENDPOINTS.ME_LOGOUT);
    return response.data;
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async (): Promise<UserResponse> => {
    const response = await apiClient.get<UserResponse>(API_ENDPOINTS.ME);
    return response.data;
  },

  /**
   * Verify email with all query params from the verification link (GET)
   */
  verifyEmail: async (
    params: Record<string, string>
  ): Promise<{ message: string }> => {
    const response = await apiClient.get<{ message: string }>(
      API_ENDPOINTS.VERIFY_EMAIL,
      { params }
    );
    return response.data;
  },

  /**
   * Get user info for complete-registration page (GET with query params from email link)
   */
  getRegistrationUserInfo: async (
    params: Record<string, string>
  ): Promise<RegistrationUserInfoResponse> => {
    const response = await apiClient.get<RegistrationUserInfoResponse>(
      API_ENDPOINTS.GET_REGISTRATION_USER_INFO,
      { params }
    );
    return response.data;
  },

  /**
   * Complete registration for admin-created user (set password and optional profile fields)
   */
  completeRegistration: async (
    data: CompleteRegistrationRequest
  ): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(
      API_ENDPOINTS.COMPLETE_REGISTRATION,
      data
    );
    return response.data;
  },
};
